import { useState, useEffect, useRef } from 'react';
import {
  Stack,
  SimpleGrid,
  Card,
  Text,
  Group,
  Title,
  Skeleton,
  Transition,
  ActionIcon,
} from '@mantine/core';
import { IconClock, IconBowlChopsticks, IconThumbUp, IconThumbDown, IconThumbUpFilled, IconThumbDownFilled } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { useAuth } from '../../hooks/useAuth';
import { FamilyProfile } from '../../types/familyProfile';
import { RecipeImage } from '../UI/RecipeImage';
import { formatTime } from '../../utils/formatTime';
import { CuisineBadge } from '../UI/CuisineBadge';

type TastePreviewRecipe = {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  cuisine: string;
  tags?: string[];
  totalTimeMinutes: number;
  difficulty: 'easy' | 'medium' | 'hard';
};

type TastePreviewProps = {
  profile: FamilyProfile;
  /** Only show on steps where taste preferences are being configured */
  visible: boolean;
};

// Profile fields that affect the preview query
const PREVIEWABLE_FIELDS: (keyof FamilyProfile)[] = [
  'cuisinePreferences',
  'favoriteProteins',
  'dietaryRestrictions',
  'preferredCookingMethods',
];

function buildPreviewKey(profile: FamilyProfile): string {
  return JSON.stringify({
    cuisinePreferences: profile.cuisinePreferences,
    avoidCuisines: profile.avoidCuisines,
    favoriteProteins: profile.favoriteProteins,
    avoidProteins: profile.avoidProteins,
    dietaryRestrictions: profile.dietaryRestrictions,
    preferredCookingMethods: profile.preferredCookingMethods,
    cookingSkill: profile.cookingSkill,
    cuisineAdventure: profile.cuisineAdventure,
    flavorProfile: profile.flavorProfile,
    allergies: profile.allergies,
    dislikes: profile.dislikes,
  });
}

function hasPreviewablePreferences(profile: FamilyProfile): boolean {
  return PREVIEWABLE_FIELDS.some((field) => {
    const value = profile[field];
    return Array.isArray(value) && value.length > 0;
  });
}

export const TastePreview = ({ profile, visible }: TastePreviewProps) => {
  const { user } = useAuth();
  const isMobile = useMediaQuery('(max-width: 47.99em)');
  const [recipes, setRecipes] = useState<TastePreviewRecipe[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const lastKeyRef = useRef<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Track votes: catalogRecipeId -> 'up' | 'down'
  const [votes, setVotes] = useState<Record<string, 'up' | 'down'>>({});
  // Recipes currently fading out
  const [fading, setFading] = useState<Set<string>>(new Set());
  // Recipes fully removed from the list
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const hasPreferences = hasPreviewablePreferences(profile);
  const shouldShow = visible && hasPreferences;
  const previewKey = buildPreviewKey(profile);
  const profileRef = useRef(profile);
  profileRef.current = profile;

  const handleVote = async (recipeId: string, vote: 'up' | 'down') => {
    if (!user || votes[recipeId] === vote) return;

    setVotes((prev) => ({ ...prev, [recipeId]: vote }));

    if (vote === 'down') {
      // Start fade-out, then remove after animation completes
      setFading((prev) => new Set(prev).add(recipeId));
      setTimeout(() => {
        setDismissed((prev) => new Set(prev).add(recipeId));
        setFading((prev) => {
          const next = new Set(prev);
          next.delete(recipeId);
          return next;
        });
      }, 400);
    }

    try {
      const token = await user.getIdToken();
      await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/taste-preview-vote`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            catalogRecipeId: recipeId,
            vote,
            source: 'onboarding',
          }),
        }
      );
    } catch (error) {
      console.error('Taste preview vote failed:', error);
      setVotes((prev) => {
        const next = { ...prev };
        delete next[recipeId];
        return next;
      });
      setFading((prev) => {
        const next = new Set(prev);
        next.delete(recipeId);
        return next;
      });
      setDismissed((prev) => {
        const next = new Set(prev);
        next.delete(recipeId);
        return next;
      });
    }
  };

  useEffect(() => {
    if (!shouldShow || !user) return;
    if (previewKey === lastKeyRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      lastKeyRef.current = previewKey;
      setLoading(true);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/taste-preview`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ familyProfile: profileRef.current }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          const newRecipes = data.recipes || [];
          setRecipes(newRecipes);
          setHasLoaded(newRecipes.length >= 3);

          // Restore thumbs-up state from server
          const ratedUpIds: string[] = data.ratedUpIds || [];
          if (ratedUpIds.length > 0) {
            const ratedUpSet = new Set(ratedUpIds);
            setVotes((prev) => {
              const next = { ...prev };
              for (const recipe of newRecipes) {
                if (ratedUpSet.has(recipe.id) && !next[recipe.id]) {
                  next[recipe.id] = 'up';
                }
              }
              return next;
            });
          }
        }
      } catch (error) {
        console.error('Taste preview fetch failed:', error);
      } finally {
        setLoading(false);
      }
    }, 1200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [previewKey, shouldShow, user]);

  // Don't render anything until we have preferences and are on a relevant step
  if (!shouldShow && !hasLoaded) return null;
  // Hide when on non-taste steps, but keep mounted so state persists
  if (!shouldShow) return null;

  const visibleRecipes = recipes.filter((r) => !dismissed.has(r.id));
  const showSkeletons = loading && recipes.length === 0;

  return (
    <Transition
      mounted={(hasLoaded && visibleRecipes.length > 0) || showSkeletons}
      transition="slide-up"
      duration={300}
    >
      {(styles) => (
        <Stack gap="sm" style={styles}>
          <Group gap="xs">
            <IconBowlChopsticks size={20} color="var(--mantine-color-orange-6)" />
            <Title order={5} c="dimmed">
              Dialing in your taste...
            </Title>
          </Group>

          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
            {showSkeletons
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Card key={i} shadow="none" withBorder radius="md" p={0}>
                    <Skeleton height={120} radius={0} />
                    <Stack gap={4} p="xs">
                      <Skeleton height={14} width="80%" />
                      <Skeleton height={10} width="50%" />
                    </Stack>
                  </Card>
                ))
              : (isMobile ? visibleRecipes.slice(0, 4) : visibleRecipes).map((recipe) => (
                  <Card
                    key={recipe.id}
                    shadow="none" withBorder
                    radius="md"
                    p={0}
                    style={{
                      overflow: 'hidden',
                      transition: 'opacity 0.35s ease, transform 0.35s ease',
                      ...(fading.has(recipe.id) && {
                        opacity: 0,
                        transform: 'scale(0.95)',
                      }),
                    }}
                  >
                    <div style={{ position: 'relative' }}>
                      <RecipeImage
                        src={recipe.imageUrl}
                        alt={recipe.name}
                        h={120}
                        w="100%"
                        fit="cover"
                      />
                      {/* Gradient scrim, matching the wizard picker cards */}
                      <div
                        style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 44,
                          background:
                            'linear-gradient(to top, rgba(0, 0, 0, 0.45), transparent)',
                          pointerEvents: 'none',
                        }}
                      />
                      <Group
                        gap={2}
                        style={{
                          position: 'absolute',
                          bottom: 4,
                          right: 4,
                          background: 'rgba(0, 0, 0, 0.5)',
                          borderRadius: 'var(--mantine-radius-xl)',
                          padding: '2px 4px',
                        }}
                      >
                        <ActionIcon
                          size="md"
                          variant="transparent"
                          color="white"
                          onClick={() => handleVote(recipe.id, 'up')}
                        >
                          {votes[recipe.id] === 'up'
                            ? <IconThumbUpFilled size={18} />
                            : <IconThumbUp size={18} />}
                        </ActionIcon>
                        <ActionIcon
                          size="md"
                          variant="transparent"
                          color="white"
                          onClick={() => handleVote(recipe.id, 'down')}
                        >
                          {votes[recipe.id] === 'down'
                            ? <IconThumbDownFilled size={18} />
                            : <IconThumbDown size={18} />}
                        </ActionIcon>
                      </Group>
                    </div>
                    <Stack gap={4} p="xs">
                      <Text size="sm" fw={600} lineClamp={2} lh={1.3}>
                        {recipe.name}
                      </Text>
                      <Group gap={6} wrap="nowrap">
                        {/* xs, not the default sm: these grid cards are a
                            good deal smaller than the Taste Tuner's. */}
                        <CuisineBadge cuisine={recipe.cuisine} size="xs" />
                        <Group gap={3} wrap="nowrap" visibleFrom="sm">
                          <IconClock
                            size={12}
                            color="var(--mantine-color-dimmed)"
                          />
                          <Text size="xs" c="dimmed">
                            {formatTime(recipe.totalTimeMinutes)}
                          </Text>
                        </Group>
                      </Group>
                      <Group gap={3} wrap="nowrap" hiddenFrom="sm">
                        <IconClock
                          size={12}
                          color="var(--mantine-color-dimmed)"
                        />
                        <Text size="xs" c="dimmed">
                          {formatTime(recipe.totalTimeMinutes)}
                        </Text>
                      </Group>
                    </Stack>
                  </Card>
                ))}
          </SimpleGrid>
        </Stack>
      )}
    </Transition>
  );
};
